import { AccountService } from "@/services/account/AccountService";
import { useToast } from "@/client/hooks/use-toast";
import useAuth from "components/modules/Auth/useAuth";
import { RouteNames } from "@/RouteNames";
import { useState } from "react";
import { useActiveSubscriptionQuery } from "@/modules/Dashboard/Pricing/useActiveSubscriptionQuery";
import { base64ToString, stringToBase64 } from "@/utils/base64Helper";
import { getCookie, removeCookie, setCookie } from "@/utils/cookies";
import { useRouter } from "next/router";

const useSubscription = (stripeProductId?: string) => {
    const router = useRouter();
    const [isBusy, setIsBusy] = useState(false);
    const { isLoggedIn } = useAuth();
    const { toast } = useToast();
    const { refetch, data: activeSubscription, promise: activeSubscriptionQueryPromise } = useActiveSubscriptionQuery();
    const hasAnyStripeSubscription = !!activeSubscription?.result?.stripeSubscriptionId;

    const refreshSubscriptionIfNeeded = async () => {
        if (!isLoggedIn()) return;
        const data = await activeSubscriptionQueryPromise;
        const stripeSubscriptionId = data?.result?.stripeSubscriptionId;
        if (stripeSubscriptionId && data?.result?.status === "incomplete") {
            try {
                const result = await AccountService.refreshSubscription(stripeSubscriptionId);
                if (result) {
                    await refetch();
                }
            } catch (e) {
                console.error(e);
            }
        }
    };

    function setDashboardRedirectAction(redirectAction: string) {
        try {
            setCookie("dashboardRedirectAction", stringToBase64(redirectAction), {
                expires: new Date(Date.now() + 1000 * 60 * 3), // 3 minutes
            });
        } catch (e) {
            console.error(e);
        }
    }

    function removeDashboardRedirectAction() {
        try {
            removeCookie("dashboardRedirectAction");
        } catch (e) {
            console.error(e);
        }
    }

    const handleDashboardRedirectAction = async () => {
        try {
            const dashboardRedirectAction = getCookie("dashboardRedirectAction");
            if (dashboardRedirectAction) {
                const data = JSON.parse(base64ToString(dashboardRedirectAction));
                if (data.type === RedirectActions.subscribe) {
                    const res = await activeSubscriptionQueryPromise;
                    const hasAnyStripeSubscription = !!res?.result?.stripeSubscriptionId;
                    if (!hasAnyStripeSubscription) {
                        handleSubscribeAction(data.planId);
                    }
                } else if (data.type === RedirectActions.freeDowngrade) {
                    // handled using  URL params
                } else if (data.type === RedirectActions.redirect) {
                    if (data.url) router.push(data.url);
                }
                removeDashboardRedirectAction();
            }
        } catch (e) {
            console.error(e);
        }
    };

    const handleSubscribeAction = async (planId?: string) => {
        if (!isLoggedIn()) {
            const redirectAction = JSON.stringify({
                type: "subscribe",
                planId: planId,
            });

            setDashboardRedirectAction(redirectAction);
            router.push(`${RouteNames.signupRoute}?redirectAction=${stringToBase64(redirectAction)}`);
            return;
        }
        if (!planId) {
            /// User clicked on a Free plan and login state and  pro user
            if (
                isLoggedIn() &&
                hasAnyStripeSubscription &&
                activeSubscription?.result?.irSubscriptionStatus == "active"
            ) {
                const redirectAction = JSON.stringify({ type: RedirectActions.freeDowngrade });
                router.push(`${RouteNames.dashboardRoute}?redirectAction=${stringToBase64(redirectAction)}`);
            } else {
                router.push(RouteNames.dashboardRoute);
            }
            return;
        }

        if (hasAnyStripeSubscription) {
            return handleManageAction({
                activeSubscriptionId: activeSubscription?.result?.stripeSubscriptionId,
                isUpgrade: activeSubscription?.result?.status === "active",
            });
        }
        if (isBusy) return;
        setIsBusy(true);

        if (!isLoggedIn()) {
            window.location.href = RouteNames.loginRoute;
            return;
        }

        const res = await AccountService.createPaymentLink({ planId: planId });
        setIsBusy(false);

        removeDashboardRedirectAction();
        console.log(res);

        if (res.error) {
            console.error(res.error);
            toast({
                title: "Error",
                variant: "destructive",
                description: res.error.message,
            });
            return;
        }
        window.location.href = res.result!.url;
        setIsBusy(false);
    };

    const handleManageAction = async ({
        activeSubscriptionId,
        isUpgrade,
    }: {
        activeSubscriptionId?: string;
        isUpgrade?: boolean;
    }) => {
        if (!isLoggedIn()) {
            router.push(RouteNames.loginRoute);
            return;
        }

        if (!activeSubscriptionId) {
            router.push(RouteNames.pricing);
            return;
        }

        if (isBusy) return;
        setIsBusy(true);

        try {
            const res = await AccountService.createCustomerPortalSession({
                activeSubscriptionId: isUpgrade ? activeSubscriptionId : undefined,
            });
            setIsBusy(false);

            console.log(res);

            if (res.error) {
                console.error(res.error);
                return;
            }

            if (!res.result) {
                toast({
                    title: "Error",
                    variant: "destructive",
                    description: "Unable to create customer portal session",
                });
                return;
            }

            if (res.result.url) {
                void router.push(res.result.url);
            }
        } catch (e) {
            console.error(e);
        }
        setIsBusy(false);
    };
    return {
        handleSubscribeAction,
        isBusy,
        handleManageAction,
        activeSubscription,
        activeSubscriptionQueryPromise,
        setDashboardRedirectAction,
        refreshSubscriptionIfNeeded,
        handleDashboardRedirectAction,
    };
};
export default useSubscription;

export const RedirectActions = {
    subscribe: "subscribe",
    freeDowngrade: "freeDowngrade",
    redirect: "redirect",
};
